import React, { useState, useRef, useEffect, useCallback } from 'react';
import './Timeline.css';
import { Dropdown, DropdownButton, DropdownItem, DropdownMenu } from '../catalyst/dropdown';
import { ChevronDownIcon } from '@heroicons/react/16/solid';
import { differenceInCalendarDays } from 'date-fns/differenceInCalendarDays';
import { format } from 'date-fns/format';
import HeaderRow from './HeaderRow';
import { Button } from '../catalyst/button';
import { addDays } from 'date-fns/addDays';
import SVGCurvedLineWithLabel from './SVGCurvedLine';
import { useLoaderData } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { projectQuery } from '../../api-v2/queries/project-query';
import TaskOverview from './TaskOverview';
import setup from './setup';
import { useUserContext } from '../../context/UserContext';

const SCROLL_SPEED = 0.4;
const ITEM_HEIGHT = 72;
const BAR_HEIGHT = 22;

function debounce(fn, ms) {
  let timer;
  return (_) => {
    clearTimeout(timer);
    timer = setTimeout((_) => {
      timer = null;
      fn.apply(this, arguments);
    }, ms);
  };
}

const Timeline = ({
  items,
  toggleGroup,
  expandedGroups,
  actions,
  archiveTaskMutation,
  completeTaskMutation,
  reopenTaskMutation,
  cloneTaskMutation,
  toggleVisibilityMutation,
  completedInThePastMutation,
  onAddTaskToGroupClick,
  onMoveTaskClick,
  TaskComponent = TaskOverview,
}) => {
  const { user } = useUserContext();
  const [view, setView] = useState(
    () => localStorage.getItem(`${user.id}:project-timeline`) || 'week',
  );
  const containerRef = useRef(null);
  const contentRef = useRef(null);
  const heightCalcRef = useRef(null);
  const [contentHeight, setContentHeight] = useState(500);
  const [currentScrollPosition, setCurrentScrollPosition] = useState(0);
  const [mousePosition, setMousePosition] = useState();
  const [showCursorDate, setShowCursorDate] = useState(false);
  // const [touchStartX, setTouchStartX] = useState(null);
  const [config, setConfig] = useState(() => {
    const selectedView = localStorage.getItem(`${user.id}:project-timeline`) || 'week';
    return setup(selectedView, {
      startDate: items[0]?.startDate,
      endDate: items[items.length - 1]?.displayEndDate,
    });
  });

  const handleWheel = useCallback(
    (event) => {
      if (Math.abs(event.deltaY) > Math.abs(event.deltaX)) {
        // Primarily vertical, ignore
        return;
      }

      event.preventDefault();

      setCurrentScrollPosition((prevScroll) => {
        let newScroll = prevScroll - event.deltaX * SCROLL_SPEED;
        // console.log(config.scrollMin, config.scrollMax, prevScroll, newScroll);
        if (newScroll < config.scrollMin) {
          return config.scrollMin;
        }

        if (newScroll > config.scrollMax) {
          return config.scrollMax;
        }

        return newScroll;
      });
    },
    [config],
  );

  useEffect(() => {
    localStorage.setItem(`${user.id}:project-timeline`, view);
    const newConfig = setup(view);
    // console.log({ newConfig: config });
    setConfig(newConfig);
    setCurrentScrollPosition(0);
  }, [view]);

  useEffect(() => {
    containerRef.current.addEventListener('wheel', handleWheel, { passive: false });
    return () => {
      containerRef.current.removeEventListener('wheel', handleWheel, { passive: false });
    };
  }, [config]);

  useEffect(() => {
    if (heightCalcRef.current) {
      setContentHeight(heightCalcRef.current.clientHeight - 15);
    }
  }, [heightCalcRef]);

  useEffect(() => {
    const debouncedHandleResize = debounce(function handleResize() {
      const newConfig = setup(view);
      setConfig(newConfig);
      setCurrentScrollPosition(0);
    }, 200);

    window.addEventListener('resize', debouncedHandleResize);

    return (_) => {
      window.removeEventListener('resize', debouncedHandleResize);
    };
  });

  const dependecies = items
    .map((item, index) => {
      if (!item.referencedFlowId || item.timing === 'Offset') {
        return null;
      }

      const dependecyIndex = items.findIndex((i) => i.id === item.referencedFlowId);
      const dependecy = items[dependecyIndex];

      if (!dependecy) {
        return null;
      }

      if (item.timing === 'Sequential' || item.timing === 'Strictly_Sequential') {
        const startX =
          !dependecy.completed &&
          dependecy.displayEndDate < config.today.toISOString().split('T')[0]
            ? config.startX + config.dayWidth
            : config.startX -
              differenceInCalendarDays(
                config.today,
                new Date(dependecy.displayEndDate || dependecy.endDate),
              ) *
                config.dayWidth +
              config.dayWidth;

        return {
          id: item.id,
          label: `${item.timing}  ${item.referencedFlowName}`,
          startX,
          startY: dependecyIndex * ITEM_HEIGHT + ITEM_HEIGHT / 2,
          endX:
            config.startX -
            differenceInCalendarDays(config.today, new Date(item.startDate)) * config.dayWidth,
          endY: index * ITEM_HEIGHT + ITEM_HEIGHT / 2,
          timing: 'after',
        };
      }

      return {
        id: item.id,
        startX:
          config.startX -
          differenceInCalendarDays(config.today, new Date(item.startDate)) * config.dayWidth,
        startY: dependecyIndex * ITEM_HEIGHT + ITEM_HEIGHT / 2,
        endX:
          config.startX -
          differenceInCalendarDays(config.today, new Date(dependecy.startDate)) * config.dayWidth,
        endY: index * ITEM_HEIGHT + ITEM_HEIGHT / 2,
        timing: 'parallel',
      };
    })
    .filter((d) => d !== null);

  // const handleTouchStart = (event) => {
  //   setTouchStartX(event.touches[0].clientX);
  // };

  // const handleTouchMove = (event) => {
  //   if (touchStartX === null) return;
  //   event.preventDefault();
  //   event.stopPropagation();

  //   const touchX = event.touches[0].clientX;
  //   const deltaX = touchStartX - touchX;

  //   setCurrentScrollPosition((prevScroll) => {
  //     let newScroll = prevScroll + deltaX * SCROLL_SPEED;
  //     const maxScroll =
  //       (contentRef.current?.scrollWidth || 0) + (containerRef.current?.clientWidth || 0);
  //     newScroll = Math.max(0, Math.min(newScroll, maxScroll));
  //     return newScroll;
  //   });
  //   setTouchStartX(touchX);
  // };

  // const handleTouchEnd = () => {
  //   setTouchStartX(null);
  // };

  const additionalItems = [
    { key: 'today', translateX: config.startX, label: format(config.today, 'd MMM') },
  ];

  if (showCursorDate) {
    additionalItems.push({
      key: 'cursor',
      translateX: mousePosition.x - currentScrollPosition,
      color: 'gray',
      label: format(
        addDays(
          config.today,
          Math.floor((mousePosition.x - currentScrollPosition - config.startX) / config.dayWidth),
        ),
        'd MMM',
      ),
    });
  }

  return (
    <div className="h-full relative flex flex-col overflow-hidden">
      <div className="flex items-center justify-end p-4 border-b gap-x-4">
        <Button
          plain
          onClick={() => {
            setCurrentScrollPosition(0);
          }}
        >
          Today
        </Button>
        <Dropdown>
          <DropdownButton outline>
            <span className="capitalize">{view}</span>
            <ChevronDownIcon />
          </DropdownButton>
          <DropdownMenu anchor="top start">
            <DropdownItem onClick={() => setView('year')}>Year</DropdownItem>
            <DropdownItem onClick={() => setView('quarter')}>Quarter</DropdownItem>
            <DropdownItem onClick={() => setView('month')}>Month</DropdownItem>
            <DropdownItem onClick={() => setView('week')}>Week</DropdownItem>
          </DropdownMenu>
        </Dropdown>
        {actions}
      </div>
      <div
        className="flex flex-1 overflow-hidden items-stretch touch-pan-x"
        // tabIndex={0}
        onKeyDown={(event) => {
          switch (event.key) {
            case 'ArrowLeft':
              setCurrentScrollPosition((v) => v + 40);
              break;
            case 'ArrowRight':
              setCurrentScrollPosition((v) => v - 40);
              break;
          }
        }}
      >
        <div
          className="flex flex-1 overflow-hidden flex-col items-stretch"
          id="scrollable-container"
          ref={(node) => {
            if (node && node !== containerRef.current) {
              containerRef.current = node;
            }
          }}
          // onTouchStart={handleTouchStart}
          // onTouchMove={handleTouchMove}
          // onTouchEnd={handleTouchEnd}
          onMouseEnter={(event) => {
            // setShowCursorDate(true);
            const { clientX, clientY } = event;
            const x = window.innerWidth < 1024 ? clientX : clientX - 256;
            setMousePosition({
              x,
              y: clientY,
            });
          }}
          onMouseLeave={() => {
            setShowCursorDate(false);
          }}
          onMouseMove={(event) => {
            const { clientX, clientY } = event;
            const x = window.innerWidth < 1024 ? clientX : clientX - 256;
            setMousePosition({
              x,
              y: clientY,
            });
          }}
        >
          <div
            id="scrollable-content"
            ref={contentRef}
            className="flex flex-1 overflow-hidden flex-col items-stretch relative"
          >
            {/* Header start */}
            <div className="relative z-10 flex">
              <div
                className="flex relative"
                style={{ width: config.containerWidth }}
              >
                <div
                  data-transform
                  className="overscrollBehaviourX relative text-xs"
                  style={{
                    transform: `translateX(${currentScrollPosition}px)`,
                    overscrollBehaviorX: 'contain',
                  }}
                >
                  <HeaderRow items={config.monthItems} />
                  <HeaderRow
                    items={config.dayItems}
                    additionalItems={additionalItems}
                  />
                  <div className="h-[16px] relative top-0"></div>
                </div>
              </div>
            </div>
            {/* Header end */}
            {(view === 'week' || view === 'month') && (
              <div
                id="weekend-indicators"
                className="absolute top-0 left-0 bottom-0"
                style={{
                  transform: `translateX(${currentScrollPosition}px)`,
                }}
              >
                {config.weekends.map((w, i) => (
                  <div
                    key={i}
                    className="absolute top-0 h-full bg-zinc-100"
                    style={{
                      width: w.width,
                      left: w.left,
                    }}
                  ></div>
                ))}
              </div>
            )}
            {/* Today marker */}
            <div
              className="absolute top-0 h-full w-[1px] bg-blue-500"
              style={{
                transform: `translateX(${currentScrollPosition}px)`,
                left: config.startX,
              }}
            ></div>
            {/* Cursor position marker */}
            {showCursorDate && (
              <div
                className="absolute top-0 h-full w-[1px] bg-gray-500"
                style={{
                  transform: `translateX(${0}px)`,
                  left: mousePosition.x,
                }}
              ></div>
            )}
            {/* overlay for height calc */}
            <div
              id="test"
              ref={heightCalcRef}
              className="absolute top-0 left-0 bottom-0 w-[500px] bg-zinc-50 after:content-[''] after:absolute after:top-[48px] after:bottom-0 after:left-[100%] after:w-[16px] after:bg-gradient-to-l after:from-transparent after:to-zinc-50 after:z-10"
            ></div>
            {/* Tasks or projects */}
            <div className="grow">
              <div
                className="relative w-full overflow-x-hidden overflow-y-auto"
                style={{
                  height: contentHeight - 48,
                }}
              >
                <div
                  style={{ height: items.length * ITEM_HEIGHT }}
                  className="relative"
                >
                  <div
                    id="dependencyIndicators"
                    className="absolute overflow-visible"
                    style={{ transform: `translateX(${currentScrollPosition}px)` }}
                  >
                    {dependecies.map((d) => {
                      return (
                        <SVGCurvedLineWithLabel
                          key={d.id}
                          x1={d.startX}
                          y1={d.startY}
                          x2={d.endX}
                          y2={d.endY}
                          label={d.label}
                          type={d.timing}
                        />
                      );
                    })}
                  </div>
                  {items.map((item, index) => {
                    return (
                      <div
                        key={item.id}
                        className="absolute left-0 h-[72px] w-full flex items-center"
                        style={{
                          top: index * ITEM_HEIGHT,
                        }}
                      >
                        <div className="bg-zinc-50 hover:bg-violet-50 w-[500px] py-2 absolute left-0 top-0 bottom-0 z-10 flex items-center">
                          <TaskComponent
                            task={item}
                            toggleGroup={toggleGroup}
                            isExpanded={expandedGroups[item.id]}
                            archiveTaskMutation={archiveTaskMutation}
                            completeTaskMutation={completeTaskMutation}
                            reopenTaskMutation={reopenTaskMutation}
                            cloneTaskMutation={cloneTaskMutation}
                            toggleVisibilityMutation={toggleVisibilityMutation}
                            completedInThePastMutation={completedInThePastMutation}
                            onAddTaskToGroupClick={onAddTaskToGroupClick}
                            onMoveTaskClick={onMoveTaskClick}
                          />
                        </div>
                        <div className="relative flex-1 flex items-center h-full">
                          {/* <div className="flex-1 relative h-full flex items-center"> */}
                          <div
                            className="absolute inset-0 flex items-center"
                            style={{ transform: `translateX(${currentScrollPosition}px)` }}
                          >
                            <div data-pointer-event>
                              {!item.completed &&
                                item.endDate < config.today.toISOString().split('T')[0] && (
                                  <div
                                    className="absolute border-r border-t border-b border-red-300 border-dashed rounded-r bg-red-100 shadow-md"
                                    style={{
                                      height: BAR_HEIGHT,
                                      width:
                                        differenceInCalendarDays(
                                          new Date(config.today),
                                          new Date(item.endDate),
                                        ) * config.dayWidth,
                                      transform: `translateX(${config.startX - (differenceInCalendarDays(config.today, new Date(item.displayEndDate || item.endDate)) - 1) * config.dayWidth}px)`,
                                    }}
                                  ></div>
                                )}
                              <div
                                className="border border-gray-400 rounded bg-white shadow-md"
                                style={{
                                  height: BAR_HEIGHT,
                                  width:
                                    (differenceInCalendarDays(
                                      new Date(item.displayEndDate || item.endDate),
                                      new Date(item.startDate),
                                    ) +
                                      1) *
                                    config.dayWidth,
                                  transform: `translateX(${config.startX - differenceInCalendarDays(config.today, new Date(item.startDate)) * config.dayWidth}px)`,
                                }}
                              >
                                <div className="absolute left-0 right-0 h-4 bottom-[calc(100%+4px)] min-w-36 whitespace-nowrap">
                                  {item.name}
                                </div>
                              </div>
                            </div>
                          </div>
                          {/* </div> */}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
            {/* Scrollbar */}
            <div className="flex overscroll-x-contain bg-zinc-50">
              <div className="relative flex-0 w-[500px]"></div>
              <div className="relative flex-1 overflow-hidden h-[15px]">
                <div
                  className="absolute top-[3px] bottom-[3px] rounded bg-gray-400"
                  style={{
                    width: calcScrollbarWidth(
                      config.scrollMin,
                      config.scrollMax,
                      document.getElementById('scrollable-content')?.clientWidth,
                      500,
                    ),
                    left: calcLeft(
                      currentScrollPosition,
                      config.scrollMin,
                      config.scrollMax,
                      document.getElementById('scrollable-content')?.clientWidth,
                      500,
                    ),
                  }}
                ></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

function calcScrollbarWidth(scrollMin, scrollMax, viewWidth, overlayWidth) {
  return Math.min(viewWidth / (scrollMax - scrollMin), 0.8) * (viewWidth - overlayWidth);
}

function calcLeft(currentScrollPosition, scrollMin, scrollMax, viewWidth, overlayWidth) {
  const scrollbarWidth = calcScrollbarWidth(scrollMin, scrollMax, viewWidth, overlayWidth);
  const actualViewWidth = viewWidth - overlayWidth;

  const scrollPercentage =
    currentScrollPosition < 0
      ? -currentScrollPosition / scrollMin
      : currentScrollPosition / scrollMax;

  const scrollbarOffset =
    currentScrollPosition < 0
      ? (50 / scrollMin) * currentScrollPosition + 50
      : (-50 / scrollMax) * currentScrollPosition + 50;

  return (
    actualViewWidth / 2 -
    (actualViewWidth / 2) * scrollPercentage -
    scrollbarWidth * (scrollbarOffset / 100)
  );
}

export default Timeline;

export function TimelinePageWrap() {
  const { projectId } = useLoaderData();
  const { data: project, refetch } = useQuery(projectQuery({ projectId, groupView: true }));
  window.refetch_project = refetch;
  const [expandedGroups, setExpandedGroups] = useState({});

  const items = (project?.tasks || [])
    .map((t) => (t.isGroup && expandedGroups[t.id] ? [t, ...t.subtasks] : t))
    .flat();

  return (
    <Timeline
      items={items}
      expandedGroups={expandedGroups}
      toggleGroup={(id, isExpanded) => {
        setExpandedGroups((prev) => {
          return {
            ...prev,
            [id]: isExpanded,
          };
        });
      }}
    />
  );
}
